import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);

  isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();
  isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  constructor(
    private readonly oauthService: OAuthService,
    private readonly router: Router,
  ) {
    window.addEventListener('storage', (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      // Noticed changes to access_token (most likely from another tab), updating isAuthenticated
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });

    /* Useful for debugging:
    this.oauthService.events.subscribe((_) => {
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });
    */
    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    this.oauthService.setupAutomaticSilentRefresh();

  }

  public runInitialLoginSequence() {
    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:
    return (
      this.oauthService
        .loadDiscoveryDocument()
        // 1. HASH LOGIN:
        // Try to log in via hash fragment after redirect back from IdServer from initImplicitFlow:
        .then(() => this.oauthService.tryLogin())
        .then(() => {
          if (this.oauthService.hasValidAccessToken()) {
            return Promise.resolve();
          }
          // 2. SILENT LOGIN:
          // Try to log in via a refresh because then we can prevent
          // needing to redirect the user:
          return this.oauthService
            .silentRefresh()
            .then(() => Promise.resolve())
            .catch((result) => {
              // Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
              // Only the ones where it's reasonably sure that sending the
              // user to the IdServer will help.
              const errorResponsesRequiringUserInteraction = [
                'interaction_required',
                'login_required',
                'account_selection_required',
                'consent_required',
              ];

              if (result?.reason && errorResponsesRequiringUserInteraction.includes(result.reason.error)) {
                // 3. ASK FOR LOGIN:
                // At this point we know for sure that we have to ask the
                // user to log in, so we redirect them to the IdServer to
                // enter credentials.
                //
                // Enable this to ALWAYS force a user to login.
                // this.login();
                //
                // Instead, we'll now do this:
                console.warn('User interaction is needed to log in, we will wait for the user to manually log in.');
                return Promise.resolve();
              }

              // We can't handle the truth, just pass on the problem to the next handler.
              return Promise.reject(result);
            });
        })
        .then(() => {
          this.isDoneLoadingSubject$.next(true);
          // Check for the strings 'undefined' and 'null' just to be sure. Our current
          // login(...) should never have this, but in case someone ever calls
          // initImplicitFlow(undefined | null) this could happen.
          let stateUrl = this.oauthService.state;
          if (stateUrl && stateUrl !== 'undefined' && stateUrl !== 'null') {
            if (stateUrl.startsWith('/') === false) {
              stateUrl = decodeURIComponent(stateUrl);
            }
            console.log(`There was state of ${stateUrl}, so we are sending you to: ${stateUrl}`);
            this.router.navigateByUrl(stateUrl);
          }
        })
        .catch(() => {
          this.isDoneLoadingSubject$.next(true);
        })
    );
  }

  public login(targetUrl?: string) {
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }
  logout() {
    this.oauthService.logOut();
  }
  refresh() {
    this.oauthService.silentRefresh();
  }

  get accessToken() {
    return this.oauthService.getAccessToken();
  }
  get claims() {
    return this.oauthService.getIdentityClaims();
  }

  isValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  isLocalMode() {
    return window.location.host.includes('localhost');
  }
}
